// Motion Blur — Photoshop-style directional blur.
//
// For each pixel we sample along a line segment of length `distance` oriented
// at `angle` degrees, then average the samples.  Number of samples =
// max(3, ceil(distance)) so we get at least one sample per pixel of travel.
//
//   - Bilinear interpolation at fractional sample positions.
//   - Clamp-to-edge for out-of-bounds coordinates.
//   - Premultiplied alpha throughout (see GaussianBlur.ts for rationale).
//   - Selection awareness is NOT handled here (applyFilterInPlace does it).

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface MotionBlurParams {
  /** Length of the blur trail in pixels (0 … 2000). Default 10. */
  distance: number;
  /** Direction in degrees (-360 … +360). 0 = right, 90 = down. Default 0. */
  angle: number;
}

// ---------------------------------------------------------------------------
// Premultiply / unpremultiply (identical to GaussianBlur.ts)
// ---------------------------------------------------------------------------

function premultiply(src: Uint8ClampedArray, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3]!;
    const af = a / 255;
    out[o] = src[o]! * af;
    out[o + 1] = src[o + 1]! * af;
    out[o + 2] = src[o + 2]! * af;
    out[o + 3] = a;
  }
  return out;
}

function unpremultiply(src: Float32Array, w: number, h: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3]!;
    if (a <= 0) {
      out[o] = 0;
      out[o + 1] = 0;
      out[o + 2] = 0;
      out[o + 3] = 0;
      continue;
    }
    const inv = 255 / a;
    out[o] = Math.round(src[o]! * inv);
    out[o + 1] = Math.round(src[o + 1]! * inv);
    out[o + 2] = Math.round(src[o + 2]! * inv);
    out[o + 3] = Math.round(a);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Bilinear sampling from a premultiplied Float32 buffer
// ---------------------------------------------------------------------------

/**
 * Sample a single premultiplied pixel at (fx, fy) with bilinear interpolation.
 * Coordinates are clamped to the image edges.
 */
function sampleBilinear(
  buf: Float32Array,
  w: number,
  h: number,
  fx: number,
  fy: number,
): [number, number, number, number] {
  // Clamp to [0, w-1] / [0, h-1].
  const cx = Math.max(0, Math.min(w - 1, fx));
  const cy = Math.max(0, Math.min(h - 1, fy));

  const x0 = Math.floor(cx);
  const y0 = Math.floor(cy);
  const x1 = Math.min(x0 + 1, w - 1);
  const y1 = Math.min(y0 + 1, h - 1);
  const fracX = cx - x0;
  const fracY = cy - y0;
  const ix = 1 - fracX;
  const iy = 1 - fracY;

  const o00 = (y0 * w + x0) * 4;
  const o10 = (y0 * w + x1) * 4;
  const o01 = (y1 * w + x0) * 4;
  const o11 = (y1 * w + x1) * 4;

  const r =
    buf[o00]! * ix * iy +
    buf[o10]! * fracX * iy +
    buf[o01]! * ix * fracY +
    buf[o11]! * fracX * fracY;

  const g =
    buf[o00 + 1]! * ix * iy +
    buf[o10 + 1]! * fracX * iy +
    buf[o01 + 1]! * ix * fracY +
    buf[o11 + 1]! * fracX * fracY;

  const b =
    buf[o00 + 2]! * ix * iy +
    buf[o10 + 2]! * fracX * iy +
    buf[o01 + 2]! * ix * fracY +
    buf[o11 + 2]! * fracX * fracY;

  const a =
    buf[o00 + 3]! * ix * iy +
    buf[o10 + 3]! * fracX * iy +
    buf[o01 + 3]! * ix * fracY +
    buf[o11 + 3]! * fracX * fracY;

  return [r, g, b, a];
}

// ---------------------------------------------------------------------------
// Motion-blur convolution
// ---------------------------------------------------------------------------

function motionBlurPass(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  distance: number,
  angleRad: number,
): void {
  const dx = Math.cos(angleRad);
  const dy = Math.sin(angleRad);
  const halfDist = distance / 2;
  const samples = Math.max(3, Math.ceil(distance));
  const invSamples = 1 / samples;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let sumA = 0;

      for (let s = 0; s < samples; s++) {
        const t = -halfDist + (distance * s) / (samples - 1);
        const fx = x + t * dx;
        const fy = y + t * dy;
        const [r, g, b, a] = sampleBilinear(src, w, h, fx, fy);
        sumR += r;
        sumG += g;
        sumB += b;
        sumA += a;
      }

      const o = (y * w + x) * 4;
      dst[o] = sumR * invSamples;
      dst[o + 1] = sumG * invSamples;
      dst[o + 2] = sumB * invSamples;
      dst[o + 3] = sumA * invSamples;
    }
  }
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const MotionBlur: Filter<MotionBlurParams> = {
  id: "motion-blur",
  displayName: "Motion Blur",
  defaultParams: { distance: 10, angle: 0 },

  apply(
    input: PixelBuffer,
    params: MotionBlurParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);

    const distance = params.distance;
    // Zero / negative distance → identity.
    if (!(distance > 0) || w === 0 || h === 0) {
      out.data.set(input.data);
      return out;
    }

    const angleRad = (params.angle * Math.PI) / 180;
    const pre = premultiply(input.data, w, h);
    const tmp = new Float32Array(w * h * 4);
    motionBlurPass(pre, tmp, w, h, distance, angleRad);
    const result = unpremultiply(tmp, w, h);
    out.data.set(result);
    return out;
  },
};
